import { schaltfeld, umspannwerk } from '../../../../utils/apiTypes';
import { schaltfeldState } from "./schaltfelderTypes";
import { AnyAction, createSlice, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { getWithAuth, putWithAuth, deleteWithAuth } from "../../../../utils/api";
import type { RootState, AppDispatch } from "../../../../app/store";
import { logError } from "../../../../utils/utils";
import { openSnackbar } from "../../../../features/snackbar/globalSnackbarSlice";
import { GridSortModel } from '@material-ui/data-grid';
import { errorHandler } from '../../../../utils/errorHandling';

export const schaltfeldDummy: schaltfeld
  = {
  name: "",
  nummer: "",
  breitengrad: 0,
  laengengrad: 0,
  umspannwerk: "",
  kommentar: "",
  zugeordnet: false
};

const initialState: schaltfeldState
  = {
  schaltfeldListe: [],
  uwList: [],
  altesSchaltfeld: schaltfeldDummy,
  aktuellesSchaltfeld: schaltfeldDummy,
  newEntry: false,
  editEntry: false,
  editDirty: 0,
  viewMode: false,
  error: [],
  listLoading: false,
  uwLoading: false,
  sorting: [
    {
      field: 'name',
      sort: 'asc',
    },
  ]
};

const schaltfelderSlice = createSlice({
  name: "schaltfeld",
  initialState,
  reducers:
  {
    updateSchaltfelder: (state, action: PayloadAction<schaltfeld>) => {
      const updateIndex = state.schaltfeldListe.findIndex(
        (x: schaltfeld) => (x.name == action.payload.name && x.umspannwerk == action.payload.umspannwerk)
      );
      const firstPart = state.schaltfeldListe.slice(0, updateIndex);
      let lastPart = Array<schaltfeld>();
      if (updateIndex + 1 < state.schaltfeldListe.length)
        lastPart = state.schaltfeldListe.slice(updateIndex + 1);
      state.schaltfeldListe = [...firstPart, action.payload, ...lastPart].sort((a, b) => a.name.localeCompare(b.name));
    },
    removeFromSchaltfelder: (state, action: PayloadAction<schaltfeld>) => {
      const updateIndex = state.schaltfeldListe.findIndex(
        (x) => x.name == action.payload.name
      );
      const firstPart = state.schaltfeldListe.slice(0, updateIndex);
      let lastPart = Array<schaltfeld>();
      if (updateIndex + 1 < state.schaltfeldListe.length)
        lastPart = state.schaltfeldListe.slice(updateIndex + 1);
      state.schaltfeldListe = [...firstPart, ...lastPart];
    },
    addAktuellesSchaltfeldToSchaltfelder: (state) => {
      const schaltfeldArray = state.schaltfeldListe;
      schaltfeldArray.push(state.aktuellesSchaltfeld);
      state.schaltfeldListe = [...schaltfeldArray].sort((a, b) => a.name.localeCompare(b.name));
    },
    setSchaltfelder: (state, action: PayloadAction<Array<schaltfeld>>) => {
      state.schaltfeldListe = action.payload;
    },
    setAktuellesSchaltfeld: (state, action: PayloadAction<schaltfeld>) => {
      state.aktuellesSchaltfeld = action.payload;
      state.altesSchaltfeld = action.payload;
    },
    setAltesSchaltfeld: (state, action: PayloadAction<schaltfeld>) => {
      state.altesSchaltfeld = action.payload;
    },
    resetAktuellesSchaltfeld: (state) => {
      state.aktuellesSchaltfeld = state.altesSchaltfeld;
    },
    setEditEntry: (state) => {
      state.editEntry = true;
    },
    resetEditEntry: (state) => {
      state.editEntry = false;
    },
    setNewEntry: (state) => {
      state.newEntry = true;
    },
    resetNewEntry: (state) => {
      state.newEntry = false;
    },
    resetEditDirty: (state) => {
      state.editDirty = 0;
    },
    setViewMode: (state) => {
      state.viewMode = true;
    },
    resetViewMode: (state) => {
      state.viewMode = false;
    },
    setError: (state, action: PayloadAction<string>) => {
      if (!state.error.includes(action.payload)) {
        state.error.push(action.payload);
      }
    },
    resetError: (state, action: PayloadAction<string>) => {
      if (state.error.includes(action.payload)) {
        state.error.splice(state.error.indexOf(action.payload), 1)
      }
    },
    setName: (state, action: PayloadAction<string>) => {
      state.aktuellesSchaltfeld.name = action.payload;
      state.editDirty =
        state.aktuellesSchaltfeld.name ==
          state.altesSchaltfeld.name
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setNummer: (state, action: PayloadAction<string>) => {
      state.aktuellesSchaltfeld.nummer = action.payload;
      state.editDirty =
        state.aktuellesSchaltfeld.nummer ==
          state.altesSchaltfeld.nummer
          ? state.editDirty - 1
          : state.editDirty + 1;
    },

    setBreitengrad: (state, action: PayloadAction<number>) => {
      state.aktuellesSchaltfeld.breitengrad = action.payload;
      state.editDirty =
        state.aktuellesSchaltfeld.breitengrad ==
          state.altesSchaltfeld.breitengrad
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setLaengengrad: (state, action: PayloadAction<number>) => {
      state.aktuellesSchaltfeld.laengengrad = action.payload;
      state.editDirty =
        state.aktuellesSchaltfeld.laengengrad ==
          state.altesSchaltfeld.laengengrad
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setUWs: (state, action: PayloadAction<Array<umspannwerk>>) => {
      state.uwList = action.payload;
    },
    setUmspannwerk: (state, action: PayloadAction<string>) => {
      state.aktuellesSchaltfeld.umspannwerk = action.payload;
      state.editDirty =
        state.aktuellesSchaltfeld.umspannwerk ==
          state.altesSchaltfeld.umspannwerk
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setKommentar: (state, action: PayloadAction<string>) => {
      state.aktuellesSchaltfeld.kommentar = action.payload;
      state.editDirty =
        state.aktuellesSchaltfeld.kommentar ==
          state.altesSchaltfeld.kommentar
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.listLoading = action.payload;
    },
    setUmspannwerkLoading: (state, action: PayloadAction<boolean>) => {
      state.uwLoading = action.payload;
    },
    setSorting: (state, action: PayloadAction<GridSortModel>) => {
      state.sorting = action.payload;
    },
  }
});


//Selectors
//======================================================================
export const selectSchaltfeldListe = (state: RootState): Array<schaltfeld> =>
  state.schaltfeld.schaltfeldListe;
export const selectAktuellesSchaltfeld = (state: RootState): schaltfeld =>
  state.schaltfeld.aktuellesSchaltfeld;
export const selectEditEntry = (state: RootState): boolean =>
  state.schaltfeld.editEntry;
export const selectNewEntry = (state: RootState): boolean => state.schaltfeld.newEntry;
export const selectEditDirty = (state: RootState): number =>
  state.schaltfeld.editDirty;
export const selectViewMode = (state: RootState): boolean => state.schaltfeld.viewMode;
export const selectError = (state: RootState): boolean => state.schaltfeld.error.length > 0;

export const selectUWs = (state: RootState): umspannwerk[] => state.schaltfeld.uwList;
export const selectListLoading = (state: RootState): boolean => state.schaltfeld.listLoading;
export const selectUmspannwerkLoading = (state: RootState): boolean => state.schaltfeld.listLoading;
export const selectSorting = (state: RootState): GridSortModel => state.schaltfeld.sorting;

//Actions
//=====================================================================

export const getSchaltfelder = () => (dispatch: AppDispatch): void => {
  dispatch(setListLoading(true));
  getWithAuth("SCHALTFELD", {})
    .then((response) => {
      dispatch(setSchaltfelder(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const updateSchaltfeld = (aktuellesSchaltfeld: schaltfeld): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth("SCHALTFELDID", {}, aktuellesSchaltfeld, aktuellesSchaltfeld.umspannwerk, aktuellesSchaltfeld.name)
    .then(() => {
      dispatch(
        openSnackbar({
          message: `Schaltfeld mit Name ${aktuellesSchaltfeld.name} wurde erfolgreich geändert.`,
          severity: "success",
          duration: 6000,
        })
      );
      dispatch(setAltesSchaltfeld(aktuellesSchaltfeld));
      dispatch(updateSchaltfelder(aktuellesSchaltfeld));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));

};

export const updateAktuellesSchaltfeld = (aktuellesSchaltfeld: schaltfeld): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setAktuellesSchaltfeld(aktuellesSchaltfeld));
  dispatch(setViewMode());
};

export const discardChanges = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellesSchaltfeld());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
};

export const discardNew = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellesSchaltfeld());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
  dispatch(resetViewMode());
};

export const startNewEntry = () => (dispatch: AppDispatch): void => {
  dispatch(setNewEntry());
  dispatch(setAktuellesSchaltfeld(schaltfeldDummy));
  dispatch(setEditEntry());
  dispatch(setViewMode());
};

export const createSchaltfeld = (aktuellesSchaltfeld: schaltfeld): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth("SCHALTFELDID", {}, aktuellesSchaltfeld, aktuellesSchaltfeld.umspannwerk, aktuellesSchaltfeld.name)
    .then(() => {
      dispatch(
        openSnackbar({
          message: `Schaltfeld mit Name ${aktuellesSchaltfeld.name} wurde erfolgreich angelegt.`,
          severity: "success",
          duration: 6000,
        })
      );

      dispatch(addAktuellesSchaltfeldToSchaltfelder());
      dispatch(setAktuellesSchaltfeld(schaltfeldDummy));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));

};

export const deleteSchaltfeld = (aktuellesSchaltfeld: schaltfeld): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  deleteWithAuth("SCHALTFELDID", {}, aktuellesSchaltfeld.umspannwerk, aktuellesSchaltfeld.name)
    .then(() => {
      dispatch(setAktuellesSchaltfeld(schaltfeldDummy));
      dispatch(removeFromSchaltfelder(aktuellesSchaltfeld));
      dispatch(resetEditEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
      dispatch(openSnackbar({
        message: `Schaltfeld mit Name ${aktuellesSchaltfeld.name} wurde erfolgreich gelöscht.`,
        severity: "success",
        duration: 6000,
      }));
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const getUWs = () => (dispatch: AppDispatch): void => {
  dispatch(setUmspannwerkLoading(true));
  getWithAuth("UMSPANNWERK", {})
    .then((response) => {
      dispatch(setUWs(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setUmspannwerkLoading(false)));
};

export const {
  removeFromSchaltfelder,
  setSchaltfelder,
  setAktuellesSchaltfeld,
  resetAktuellesSchaltfeld,
  setAltesSchaltfeld,
  updateSchaltfelder,
  setEditEntry,
  resetEditEntry,
  setNewEntry,
  resetNewEntry,
  resetEditDirty,
  setViewMode,
  resetViewMode,
  setError,
  resetError,
  setName,
  setNummer,
  setBreitengrad,
  setLaengengrad,
  setUmspannwerk,
  setKommentar,
  setUWs,
  addAktuellesSchaltfeldToSchaltfelder,
  setListLoading,
  setUmspannwerkLoading,
  setSorting
} = schaltfelderSlice.actions;
export default schaltfelderSlice.reducer;